import type { ReactNode } from 'react'

import {
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TableScrollArea,
  Text,
} from '@enechain/ecloud-designsystem'

import type { EnumBadgeSpec } from '~/components/common/EnumStatusBadge'

/**
 * 差分プレビューの行の種類 (common.md 行レベルバリデーション・エラー仕様の 5 ステータス)。
 * data type ごとの proto enum を、 行の地色とステータスバッジの色を決めるこの 5 種へ写す。
 */
export type CsvDiffTone = 'unchanged' | 'new' | 'changed' | 'deleted' | 'error'

/**
 * 行の地色 (design `.up-diff-row.is-keep / is-new / is-changed / is-delete / is-error`)。
 * 削除はエラー (赤地 = 問題) と区別して中立の地にし、 赤はバッジだけが持つ。
 */
const TONE_ROW_BACKGROUND: Record<CsvDiffTone, string> = {
  unchanged: 'Surface.secondary',
  new: 'Surface.positiveLow',
  changed: 'Surface.warningLow',
  deleted: 'Surface.secondary',
  error: 'Surface.negativeLow',
}

/** ステータスバッジの色 (design `.badge-neutral / badge-ok / badge-warn / badge-error`)。 */
const TONE_BADGE_STATUS: Record<CsvDiffTone, EnumBadgeSpec['status']> = {
  unchanged: 'neutral',
  new: 'positive',
  changed: 'warning',
  deleted: 'negative',
  error: 'negative',
}

/**
 * 行の種類からステータスバッジの表示仕様を作る (`EnumStatusBadge` の map 用)。
 * 行の地色とバッジの色を同じ写像から引き、 画面ごとに食い違わないようにする。
 * エラーは色に加えて警告アイコンでも示す (design のエラーバッジ)。
 */
export function csvDiffBadgeSpec(
  tone: CsvDiffTone,
  labelKey: string,
): EnumBadgeSpec {
  return {
    status: TONE_BADGE_STATUS[tone],
    labelKey,
    warningIcon: tone === 'error' ? true : undefined,
  }
}

export type CsvDiffTableColumn<T> = {
  key: string
  header: ReactNode
  /** 数値 = 右、 固定の区分値 = 中央、 それ以外 = 左 (既定)。 */
  align?: 'left' | 'right' | 'center'
  /** 主キーの列 (取引 ID・供給地点特定番号等) を等幅で出し、 桁を読み比べやすくする。 */
  mono?: boolean
  cell: (row: T) => ReactNode
}

type Props<T> = {
  columns: Array<CsvDiffTableColumn<T>>
  rows: T[]
  getRowKey: (row: T) => string
  /** 行の種類。 undefined (UNSPECIFIED 等) は地色を付けない。 */
  getRowTone: (row: T) => CsvDiffTone | undefined
}

/**
 * CSV アップロード Step2 の差分表 (design `#diff-wrap .up-diff-*`)。
 *
 * - 行は種類ごとの地色で塗り分ける (common.md「ステータス別の色分け表示」)。
 *   design は太い左罫線を使わず地色だけで分ける
 * - 見出しは表見出しの地色・中強調の文字・semibold、 セルは上下 8px / 左右 12px・md
 * - 縦に長い時は 480px で切って表の中でスクロールし、 見出しは上端に残す
 */
export const CsvDiffTable = <T,>({
  columns,
  rows,
  getRowKey,
  getRowTone,
}: Props<T>): React.ReactElement => (
  <TableScrollArea
    maxHeight="480px"
    background="Surface.primary"
    borderWidth="1px"
    borderColor="Border.lowEmphasis"
    borderRadius="md"
  >
    {/* `size="sm"` は root に 12px の textStyle を当てるので fontSize で md に戻す
        (DS に table recipe は無く素の Chakra recipe が効く)。 */}
    <TableRoot fontSize="md" size="sm" stickyHeader>
      <TableHeader>
        <TableRow>
          {columns.map((col) => (
            <TableColumnHeader
              key={col.key}
              textStyle="tableHeader"
              color="Text.midEmphasis"
              textAlign={col.align ?? 'left'}
              background="Surface.tableHeader"
              borderBottomColor="Border.lowEmphasis"
              whiteSpace="nowrap"
              paddingX={3}
              paddingY={2}
            >
              {col.header}
            </TableColumnHeader>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const tone = getRowTone(row)
          return (
            <TableRow
              key={getRowKey(row)}
              background={
                tone === undefined ? undefined : TONE_ROW_BACKGROUND[tone]
              }
            >
              {columns.map((col) => (
                // 本体セルは折り返す (design `.up-diff-row > div` は折り返し可)。
                // 素の Chakra の表は nowrap で、 列の多い画面では備考列が横スクロールの
                // 外に押し出されて見えなくなる。 見出しは nowrap のまま。
                <TableCell
                  key={col.key}
                  fontFamily={col.mono === true ? 'mono' : undefined}
                  textAlign={col.align ?? 'left'}
                  borderBottomColor="Border.lowEmphasis"
                  whiteSpace="normal"
                  paddingX={3}
                  paddingY={2}
                >
                  {col.cell(row)}
                </TableCell>
              ))}
            </TableRow>
          )
        })}
      </TableBody>
    </TableRoot>
  </TableScrollArea>
)

/**
 * 変更行の 変更前 → 変更後 (common.md「変更行は変更前 → 変更後の値をインラインで表示」、
 * design `.up-cell-old / .up-cell-arrow / .up-cell-new`)。 変更前は取り消し線の低強調、
 * 変更後は semibold で、 どちらが新しい値かを色と線で読ませる。
 * 矢印の前後は空白の文字で空ける (セルの文字列として「変更前 → 変更後」 のまま読める)。
 */
export const CsvDiffValueChange = ({
  before,
  after,
}: {
  before: string
  after: string
}): React.ReactElement => (
  <>
    <Text as="span" color="Text.lowEmphasis" textDecoration="line-through">
      {before}
    </Text>{' '}
    <Text as="span" color="Text.midEmphasis">
      →
    </Text>{' '}
    <Text as="span" color="Text.highEmphasis" fontWeight="semibold">
      {after}
    </Text>
  </>
)

/**
 * 備考列のエラー内容 (common.md「エラー内容を『備考』列に表示する」、
 * design `.up-cell-error`)。
 */
export const CsvDiffErrorText = ({
  children,
}: {
  children: ReactNode
}): React.ReactElement => (
  <Text as="span" color="Text.negativeHigh" fontWeight="semibold">
    {children}
  </Text>
)
